import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Paperclip, ArrowLeft, Plus, Send, X } from "lucide-react";

const MAX_BYTES = 10 * 1024 * 1024;
const STATUS_STYLE = {
  cree: "bg-blue-500/15 text-blue-600",
  en_cours: "bg-amber-500/15 text-amber-600",
  attente_info: "bg-purple-500/15 text-purple-600",
  cloture: "bg-emerald-500/15 text-emerald-600",
};
const STATUS_LABEL = {
  cree: "Créé", en_cours: "En cours de résolution", attente_info: "En attente d'information", cloture: "Clôturé",
};

const fmtDate = (s) => (s ? new Date(s).toLocaleString("fr-FR") : "");
const fmtSize = (n) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} Ko` : `${(n / 1048576).toFixed(1)} Mo`);

export function StatusBadge({ status }) {
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[status] || ""}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function FilePicker({ files, setFiles }) {
  const ref = useRef(null);
  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const ok = [];
    for (const f of picked) {
      if (f.size > MAX_BYTES) toast.error(`« ${f.name} » dépasse 10 Mo`);
      else ok.push(f);
    }
    const all = [...files, ...ok];
    if (all.length > 5) toast.error("5 fichiers maximum par message");
    setFiles(all.slice(0, 5));
  };
  return (
    <div>
      <input ref={ref} type="file" multiple hidden onChange={onPick} data-testid="support-file-input" />
      <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()}>
        <Paperclip size={14} className="mr-1" /> Joindre des fichiers (10 Mo max chacun)
      </Button>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between text-xs bg-muted rounded px-2 py-1">
              <span className="truncate">{f.name} · {fmtSize(f.size)}</span>
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Retirer"><X size={12} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

async function downloadFile(ticketId, f) {
  try {
    const r = await api.get(`/support/tickets/${ticketId}/files/${f.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = f.filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) {
    toast.error("Téléchargement impossible");
  }
}

function ListView() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState(null);
  useEffect(() => {
    api.get("/support/tickets").then((r) => setTickets(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);
  return (
    <div className="space-y-4 max-w-4xl">
      <Button onClick={() => navigate("/assistance/nouveau")} data-testid="support-new-ticket">
        <Plus size={16} className="mr-1" /> Créer un ticket
      </Button>
      {tickets === null ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : tickets.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun ticket pour l'instant.</p>
      ) : (
        <ul className="space-y-2">
          {tickets.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => navigate(`/assistance/${t.id}`)}
                className="w-full text-left border rounded-lg p-4 hover:bg-accent transition-colors"
                data-testid={`support-ticket-${t.number}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">
                    {t.number}
                    {t.unread_client && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-red-500" title="Nouvelle réponse" />}
                  </span>
                  <StatusBadge status={t.status} />
                </div>
                <p className="mt-1 truncate">{t.titre}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{fmtDate(t.updated_at)}</p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewView() {
  const navigate = useNavigate();
  const [titre, setTitre] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("titre", titre);
      fd.append("description", description);
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/support/tickets", fd);
      toast.success(`Ticket ${r.data.number} créé`);
      navigate(`/assistance/${r.data.id}`, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={create} className="space-y-4 max-w-4xl">
      <Button type="button" variant="ghost" size="sm" onClick={() => navigate("/assistance")}><ArrowLeft size={14} className="mr-1" /> Retour</Button>
      <div>
        <label className="text-sm font-medium">Intitulé du problème</label>
        <Input value={titre} onChange={(e) => setTitre(e.target.value)} maxLength={200} required data-testid="support-titre" />
      </div>
      <div>
        <label className="text-sm font-medium">Description</label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={14} required data-testid="support-description" />
      </div>
      <FilePicker files={files} setFiles={setFiles} />
      <Button type="submit" disabled={busy} data-testid="support-submit">Envoyer le ticket</Button>
    </form>
  );
}

function ThreadView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/support/tickets/${id}`);
      setCurrent((c) => (!c || r.data.messages.length !== c.messages.length || r.data.status !== c.status ? r.data : c));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [current?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim() && files.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("text", reply);
      files.forEach((f) => fd.append("files", f));
      const r = await api.post(`/support/tickets/${id}/messages`, fd);
      setCurrent(r.data); setReply(""); setFiles([]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!current) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <Button type="button" variant="ghost" size="sm" onClick={() => navigate("/assistance")}><ArrowLeft size={14} className="mr-1" /> Mes tickets</Button>
        <StatusBadge status={current.status} />
      </div>
      <h2 className="text-lg font-semibold">{current.number} — {current.titre}</h2>
      <div className="space-y-2 border rounded-lg p-3" data-testid="support-thread">
        {current.messages.map((m) =>
          m.author_type === "system" ? (
            <p key={m.id} className="text-center text-xs text-muted-foreground">{m.text} · {fmtDate(m.created_at)}</p>
          ) : (
            <div key={m.id} className={`flex ${m.author_type === "client" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.author_type === "client" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                <p className="text-xs opacity-70 mb-1">{m.author_label} · {fmtDate(m.created_at)}</p>
                {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                {m.files?.map((f) => (
                  <button key={f.id} type="button" onClick={() => downloadFile(current.id, f)} className="mt-1 flex items-center gap-1 text-xs underline">
                    <Paperclip size={12} /> {f.filename} ({fmtSize(f.size)})
                  </button>
                ))}
              </div>
            </div>
          ),
        )}
        <div ref={endRef} />
      </div>
      {current.status === "cloture" ? (
        <p className="text-sm text-muted-foreground">Ce ticket est clôturé. Créez un nouveau ticket si besoin.</p>
      ) : (
        <form onSubmit={send} className="space-y-2">
          <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4} placeholder="Votre message…" data-testid="support-reply" />
          <FilePicker files={files} setFiles={setFiles} />
          <Button type="submit" disabled={busy} data-testid="support-send"><Send size={14} className="mr-1" /> Envoyer</Button>
        </form>
      )}
    </div>
  );
}

export default function SupportPage({ mode = "list" }) {
  const title = mode === "new" ? "Créer un ticket" : mode === "thread" ? "Ticket d'assistance" : "Besoin d'aide";
  return (
    <Layout title={title}>
      {mode === "list" && <ListView />}
      {mode === "new" && <NewView />}
      {mode === "thread" && <ThreadView />}
    </Layout>
  );
}
